import React, { useMemo } from "react";
import cx from "classnames";
import { captureMessage } from "@sentry/gatsby";
import { useIsMobile } from "@/hooks/useDisplay";
import { HeadToHeadRow } from "./HeadToHeadRow";
import { StackedOutcomes } from "./StackedOutcomes";
import {
  WinningMarginOutcomes,
  canRenderWinningMargin,
} from "./WinningMarginOutcomes";
import { PlayerMarketRows } from "./PlayerMarketRows";
import { HomeAwayColumns } from "@/features/betting/components/outcomes/HomeAwayColumns";
import type { OutcomeLayoutProps } from "./outcomeLayout";
import type { MarketFilterRenderStyle } from "@/hooks/firestore/models/bettingMarketFilters";
import {
  isColumnGridRenderStyle,
  isHomeAwayColumnGrid,
  isHorizontalSegmentRenderStyle,
} from "@/hooks/firestore/models/bettingMarketFilters";

import * as styles from "./Outcomes.module.scss";

export const OUTCOME_NAMES = [
  "2:0",
  "0:2",
  "1:1",
  "2:1",
  "1:2",
  "3:0",
  "0:3",
  "3:1",
  "1:3",
  "3:2",
  "2:3",
];

const excludedHeadToHeadMarkets = [
  // regex list contains DOUBLECHANCE
  /DOUBLECHANCE/,
];

const reportedHeadToHeadMismatches = new Set<string>();

// warns when config says HEAD_TO_HEAD but the outcome shape doesn't actually
// fit it - once per market per session
const reportHeadToHeadShapeMismatch = (
  marketId?: string,
  marketType?: string,
) => {
  const dedupeKey = marketId ?? marketType ?? "unknown";
  if (reportedHeadToHeadMismatches.has(dedupeKey)) return;
  reportedHeadToHeadMismatches.add(dedupeKey);

  captureMessage(
    "HEAD_TO_HEAD render style configured for a market whose outcome shape doesn't fit it",
    {
      level: "warning",
      fingerprint: ["head-to-head-shape-mismatch", marketType ?? "unknown"],
      tags: { marketType },
      extra: { marketId },
    },
  );
};

type OutcomesProps = OutcomeLayoutProps & {
  marketType?: string;
  marketCategory?: string;
  fullWidth?: boolean;
  // how the filters config wants this group laid out - takes priority over
  // the shape heuristics below. Absent for callers with no config (race,
  // pickems, ...) or groups it doesn't cover
  renderStyle?: MarketFilterRenderStyle;
  // key for the active search/type filter forcing "show more" open, null when none active
  forceExpand?: string | null;
};

const Outcomes = ({
  outcomes,
  competitors,
  eventId,
  eventStatus,
  oddsFormat,
  selections = [],
  toggleSelection,
  className,
  isSuspended = false,
  isOutright = false,
  status,
  isLoading = false,
  mainMarket,
  liftTeamNames = false,
  marketId: marketIdProp,
  marketType: marketTypeProp,
  marketCategory: marketCategoryProp,
  fullWidth = false,
  renderStyle,
  forceExpand,
}: OutcomesProps) => {
  const isMobile = useIsMobile();

  // props win, otherwise read it off the first outcome's market
  const market = useMemo(() => Object.values(outcomes)[0]?.market, [outcomes]);
  const marketId = marketIdProp ?? market?.id;
  const marketType = marketTypeProp ?? market?.marketType;
  const marketCategory = marketCategoryProp ?? market?.category;

  const homeOutcomeId = useMemo(
    () =>
      Object.keys(outcomes).find((outcomeId) =>
        outcomes[outcomeId]?.type?.endsWith("_HOME"),
      ),
    [outcomes],
  );
  const awayOutcomeId = useMemo(
    () =>
      Object.keys(outcomes).find((outcomeId) =>
        outcomes[outcomeId]?.type?.endsWith("_AWAY"),
      ),
    [outcomes],
  );
  const drawOutcomeId = useMemo(
    () =>
      Object.keys(outcomes).find(
        (outcomeId) => outcomes[outcomeId].type === "DRAW",
      ),
    [outcomes],
  );

  const isPromo = marketCategory === "Promotions";
  const isCorrectScore = marketType?.includes("CORRECT_SCORE");
  const isFullWidth = fullWidth || isPromo || (isMobile && isCorrectScore);
  // When we have 3 outcomes and there are home and away markets available, we need to make sure that
  // last market is a draw, otherwise we can't render it as h2h
  const missingDrawOutcome =
    !drawOutcomeId && Object.keys(outcomes).length === 3;

  const canRenderHeadToHead =
    (homeOutcomeId || awayOutcomeId) &&
    !missingDrawOutcome &&
    Object.keys(outcomes).length <= 3 &&
    !excludedHeadToHeadMarkets.some((regex) => regex.test(marketType));

  // the attribute key this render style groups/columns by (e.g. "side",
  // "player") - computed once so every branch below agrees
  let renderFieldsKey: string | undefined;
  if (isColumnGridRenderStyle(renderStyle)) {
    renderFieldsKey = renderStyle.columnConfiguration.key;
  } else if (isHorizontalSegmentRenderStyle(renderStyle)) {
    renderFieldsKey = renderStyle.groupBy.key;
  }

  // margin/threshold grouping is a within-market shape, not a render style -
  // checked regardless of renderStyle, same as before
  if (
    marketType?.includes("WINNING_MARGIN") &&
    canRenderWinningMargin(outcomes)
  ) {
    return (
      <WinningMarginOutcomes
        outcomes={outcomes}
        competitors={competitors}
        eventId={eventId}
        eventStatus={eventStatus}
        marketId={marketId}
        oddsFormat={oddsFormat}
        selections={selections}
        toggleSelection={toggleSelection}
        className={className}
        isSuspended={isSuspended}
        isOutright={isOutright}
        status={status}
        isLoading={isLoading}
        mainMarket={mainMarket}
        renderFieldsKey={renderFieldsKey}
      />
    );
  }

  if (
    isHomeAwayColumnGrid(renderStyle) &&
    competitors &&
    (homeOutcomeId || awayOutcomeId)
  ) {
    return (
      <HomeAwayColumns
        outcomes={outcomes}
        competitors={competitors}
        eventId={eventId}
        eventStatus={eventStatus}
        oddsFormat={oddsFormat}
        selections={selections}
        toggleSelection={toggleSelection}
        isLoading={isLoading}
        mainMarket={mainMarket}
        renderFieldsKey={renderFieldsKey}
        forceExpand={forceExpand}
      />
    );
  }

  if (
    isHorizontalSegmentRenderStyle(renderStyle) &&
    renderFieldsKey === "player" &&
    competitors
  ) {
    return (
      <PlayerMarketRows
        outcomes={outcomes}
        competitors={competitors}
        eventId={eventId}
        eventStatus={eventStatus}
        oddsFormat={oddsFormat}
        selections={selections}
        toggleSelection={toggleSelection}
        isLoading={isLoading}
        mainMarket={mainMarket}
        renderFieldsKey={renderFieldsKey}
        isSuspended={isSuspended}
        forceExpand={forceExpand}
      />
    );
  }

  // HEAD_TO_HEAD from config, or the shape heuristic with no config. Still
  // gated by canRenderHeadToHead - a mismatch falls through to the stacked
  // grid instead of a broken row, and gets reported
  const configWantsHeadToHead = renderStyle?.type === "HEAD_TO_HEAD";
  const wantsHeadToHead = renderStyle
    ? configWantsHeadToHead
    : !!canRenderHeadToHead;

  if (configWantsHeadToHead && !canRenderHeadToHead) {
    reportHeadToHeadShapeMismatch(marketId, marketType);
  }

  // full-width markets fall through to the stacked grid - head-to-head's
  // fixed 3-column row has no full-width variant
  if (wantsHeadToHead && canRenderHeadToHead && !isFullWidth) {
    return (
      <div className={cx(styles.outcomes, className)}>
        <HeadToHeadRow
          outcomes={outcomes}
          competitors={competitors}
          eventId={eventId}
          eventStatus={eventStatus}
          marketId={marketId}
          oddsFormat={oddsFormat}
          selections={selections}
          toggleSelection={toggleSelection}
          isSuspended={isSuspended}
          isOutright={isOutright}
          status={status}
          isLoading={isLoading}
          mainMarket={mainMarket}
          homeOutcomeId={homeOutcomeId}
          awayOutcomeId={awayOutcomeId}
          drawOutcomeId={drawOutcomeId}
          renderFieldsKey={renderFieldsKey}
        />
      </div>
    );
  }

  // STACKED_GRID
  return (
    <StackedOutcomes
      outcomes={outcomes}
      competitors={competitors}
      eventId={eventId}
      eventStatus={eventStatus}
      marketId={marketId}
      oddsFormat={oddsFormat}
      selections={selections}
      toggleSelection={toggleSelection}
      className={className}
      isSuspended={isSuspended}
      isOutright={isOutright}
      status={status}
      isLoading={isLoading}
      mainMarket={mainMarket}
      liftTeamNames={liftTeamNames}
      marketType={marketType}
      fullWidth={isFullWidth}
      renderFieldsKey={renderFieldsKey}
    />
  );
};

export { Outcomes };
